import { Box, Button, HStack, Text, useToast, VStack } from "@chakra-ui/react";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { RiArrowRightSLine, RiRecordCircleLine } from "react-icons/ri";
import { AudioFile, db, Phrase, Student } from "../db";
import { RecordingMode, RecordingOverrideControls } from "../Workbench";
import AudioVisualiser, { VisualiserControls } from "./AudioVisualiser";
import RecordingFeedback, { CountdownControls } from "./RecordingFeedback";
import { createRecordingTools } from "./recordingTools";

export const AUTO_DELAY = 250;
export type ToastStatus = "success" | "error" | "warning" | "info" | "loading";

interface ActiveStudentPhraseProps {
  student: Student;
  phrase: Phrase;
  fetchAudio: (
    studentId: number,
    phrase: string
  ) => Promise<AudioFile | undefined>;
  onAudioRecorded: (audio: AudioFile) => Promise<void>;
  onNextStudentPhrase: () => Promise<void>;
  recordingMode: RecordingMode;
  countdownSetting: number;
  recordingOverrideCallbackRef: MutableRefObject<RecordingOverrideControls | null>;
  sampleLength: number;
  updateStudents: () => void;
}

const ActiveStudentPhrase = ({
  student,
  phrase,
  fetchAudio,
  onAudioRecorded,
  onNextStudentPhrase,
  recordingMode,
  countdownSetting,
  recordingOverrideCallbackRef,
  sampleLength,
  updateStudents,
}: ActiveStudentPhraseProps) => {
  const [controlsDisabled, setControlsDisabled] = useState<boolean>(false);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [audioFile, setAudioFile] = useState<AudioFile>();
  // TODO: loading state
  useEffect(() => {
    const load = async (studentId: number, phrase: string) => {
      setAudioFile(await fetchAudio(studentId, phrase));
    };
    load(student.id!, phrase.text);
  }, [student, phrase, fetchAudio]);

  // TODO: Extract this somehow.
  const toast = useToast();
  const customToast = useCallback(
    (title: string, description: string, status: ToastStatus) => {
      toast({
        title,
        description,
        status,
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
    },
    [toast]
  );

  const automateRecording = useRef<boolean>(false);
  const visualizerCallbackRef = useRef<VisualiserControls | null>(null);
  const countdownCallbackRef = useRef<CountdownControls | null>(null);
  const startRecordingTimeout = useRef<number>();
  const stopRecordingTimeout = useRef<number>();

  const handleNext = useCallback(async () => {
    setAudioFile(undefined);
    await onNextStudentPhrase();
  }, [onNextStudentPhrase]);

  const handleRecord = useCallback(
    async (user: boolean) => {
      if (recordingMode === "auto" && user) {
        // Record has been pressed manually after settings play mode to auto.
        automateRecording.current = true;
      }
      const chunks: Blob[] = [];
      const tools = await createRecordingTools(customToast);
      if (!tools) {
        return;
      }
      const { mediaRecorder, analyser } = tools;
      if (mediaRecorder.state === "recording") {
        throw new Error("Recording already in progress");
      }

      mediaRecorder.ondataavailable = async (event) => {
        chunks.push(event.data);
      };

      const stopListener = async () => {
        const blob = new Blob(chunks, { type: "audio/wav" });
        const updatedAudioFile = {
          id: audioFile?.id,
          data: blob,
          phrase: phrase.text,
          studentId: student.id!,
          waveformImg: visualizerCallbackRef.current?.toDataURL(),
          sampleLength: sampleLength,
        };
        await onAudioRecorded(updatedAudioFile);
        setAudioFile(updatedAudioFile);
        countdownCallbackRef.current?.stopCountdown();
        setControlsDisabled(false);
        setIsRecording(false);
        if (automateRecording.current) {
          autoRecordFired.current = false;
          await handleNext();
        }
      };

      mediaRecorder.addEventListener("stop", stopListener);

      recordingOverrideCallbackRef.current = {
        forceStop() {
          automateRecording.current = false;
          clearTimeout(startRecordingTimeout.current);
          clearTimeout(stopRecordingTimeout.current);
          countdownCallbackRef.current?.stopCountdown();
          visualizerCallbackRef.current?.forceStopRecording();
          mediaRecorder.removeEventListener("stop", stopListener);
          mediaRecorder.stop();
          setControlsDisabled(false);
          setIsRecording(false);
        },
      };

      setControlsDisabled(true);
      countdownCallbackRef.current?.startCountdown();
      startRecordingTimeout.current = setTimeout(() => {
        setIsRecording(true);
        mediaRecorder.start();
        visualizerCallbackRef.current?.start(analyser);
        stopRecordingTimeout.current = setTimeout(() => {
          mediaRecorder.stop();
        }, sampleLength);
      }, Math.max(countdownSetting * 1000 + 100, AUTO_DELAY));
      // TODO media error handling?
    },
    [
      automateRecording,
      audioFile,
      countdownSetting,
      customToast,
      handleNext,
      recordingMode,
      phrase,
      student.id,
      onAudioRecorded,
      recordingOverrideCallbackRef,
      sampleLength,
    ]
  );

  useEffect(() => {
    if (recordingMode === "default") {
      automateRecording.current = false;
    }
  }, [automateRecording, recordingMode]);

  const autoRecordFired = useRef<boolean>(false);
  useEffect(() => {
    if (automateRecording.current) {
      if (!autoRecordFired.current) {
        autoRecordFired.current = true;
        handleRecord(false);
      }
    }
  }, [automateRecording, handleRecord]);

  const parentRef = useRef<HTMLDivElement>(null);

  const audioElementRef = useRef<HTMLAudioElement>(null);
  const handleAudioPlay = useCallback(() => {
    audioElementRef.current?.play();
  }, []);

  useEffect(() => {
    const keydown = (e: KeyboardEvent) => {
      const openDialog = !!document.querySelector("[role='dialog'");
      if (!openDialog) {
        if (e.key === "P" || e.key === "p") {
          handleRecord(true);
        }
        if (e.key === "N" || e.key === "n") {
          handleNext();
        }
      }
    };

    document.addEventListener("keydown", keydown);
    return () => {
      document.removeEventListener("keydown", keydown);
    };
  });

  const onDeleteRecording = useCallback(async () => {
    try {
      await db.audioFiles.delete(audioFile?.id!);
      const studentPhrases = [...student.phrases].filter(
        (p) => p !== phrase.text
      );
      await db.students.update(student.id!, { phrases: studentPhrases });
      setAudioFile(undefined);
      await updateStudents();
    } catch (err) {
      console.error(`Failed to delete recording: ${err}`);
    }
  }, [audioFile, student, phrase, updateStudents]);

  return (
    <VStack spacing={10} width="100%">
      <VStack width="100%">
        <Text fontSize="3rem" color="blue.600" fontWeight="semibold">
          {student.name}
        </Text>
        <Text fontSize="4xl">get ready to say</Text>
        <Text fontSize="3rem" color="blue.600" fontWeight="semibold">
          {phrase.text} {phrase.emoji}
        </Text>
      </VStack>
      <Box
        ref={parentRef}
        borderRadius="lg"
        overflow="hidden"
        width="100%"
        height="150px"
        position="relative"
      >
        {isRecording && (
          <Box
            bgColor="green.200"
            opacity={0.4}
            height="100%"
            width="100%"
            position="absolute"
            top={0}
            left={0}
            zIndex={0}
          />
        )}
        <AudioVisualiser
          key={`${student.id}-${phrase}`}
          callbackRef={visualizerCallbackRef}
          canvasWidth={parentRef.current?.clientWidth}
          sampleLength={sampleLength}
        />
        {!isRecording && (
          <RecordingFeedback
            callbackRef={countdownCallbackRef}
            audioFile={audioFile}
            countdownSetting={countdownSetting}
            onAudioPlay={handleAudioPlay}
            audioElementRef={audioElementRef}
            onDeleteRecording={onDeleteRecording}
          />
        )}
      </Box>
      <HStack>
        <Button
          leftIcon={<RiRecordCircleLine />}
          onClick={() => handleRecord(true)}
          colorScheme={!audioFile ? "blue" : undefined}
          isDisabled={controlsDisabled}
          width="8em"
          transition="none"
        >
          {isRecording ? "Recording" : "Record"}
        </Button>
        <Button
          onClick={handleNext}
          rightIcon={<RiArrowRightSLine />}
          colorScheme={audioFile ? "blue" : undefined}
          transition="none"
          isDisabled={controlsDisabled}
        >
          Next
        </Button>
      </HStack>
      {audioFile && (
        <audio
          ref={audioElementRef}
          style={{ display: "none" }}
          src={window.URL.createObjectURL(audioFile.data)}
        />
      )}
      <Button
        colorScheme={"red"}
        disabled={!automateRecording.current && !controlsDisabled}
        onClick={recordingOverrideCallbackRef.current?.forceStop}
      >
        Stop recording
      </Button>
    </VStack>
  );
};

export default ActiveStudentPhrase;
